import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, Treemap, Tooltip } from "recharts";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtPct, fmtSigned, fmtCurrency, gainClass } from "@/lib/format";
import { ChevronLeft } from "lucide-react";

const MODES = [{ key: "subs", label: "Sub-portfolios" }, { key: "stocks", label: "Stocks" }];
const PERIODS = [{ key: "1D", label: "Today" }, { key: "1W", label: "Week" }, { key: "1M", label: "Month" }, { key: "YTD", label: "YTD" }];
const TITLES = { "1D": "Today's Heatmap", "1W": "Past Week Heatmap", "1M": "Past Month Heatmap", YTD: "Year-to-Date Heatmap" };
// % move at which colour reaches full strength, per period
const SATURATE = { "1D": 3, "1W": 6, "1M": 10, YTD: 25 };

const stored = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
const store = (k, v) => { try { localStorage.setItem(k, v); } catch { /* storage unavailable */ } };

function tileColor(pct, sat) {
    if (pct == null || Math.abs(pct) < 0.005) return "hsl(var(--muted-foreground) / 0.35)";
    const a = 0.6 + Math.min(Math.abs(pct) / sat, 1) * 0.4;
    return pct > 0 ? `hsl(var(--gain) / ${a})` : `hsl(var(--loss) / ${a})`;
}

function Tile({ x, y, width, height, name, pct, dollar, mask, onOpen, sat, active }) {
    if (!name || width <= 0 || height <= 0) return null;
    const big = width > 90 && height > 46;
    const on = active === name;
    return (
        <g data-testid={`heatmap-tile-${name}`} data-active={on} onClick={onOpen ? () => onOpen(name) : undefined} style={onOpen ? { cursor: "pointer" } : undefined}>
            <rect x={x} y={y} width={width} height={height} rx={6} fill={tileColor(pct, sat)} stroke={on ? "hsl(var(--foreground))" : "hsl(var(--background))"} strokeWidth={3} />
            {width > 44 && height > 22 && (
                <text x={x + 8} y={y + (big ? 20 : height / 2 + 4)} fill="#fff" fontSize={big ? 13 : 11} fontWeight={600}>
                    {name.length * 7 > width - 12 ? `${name.slice(0, Math.max(3, Math.floor((width - 12) / 7)))}…` : name}
                </text>
            )}
            {big && <text x={x + 8} y={y + 38} fill="#fff" fontSize={12} fontWeight={500}>{fmtPct(pct)}</text>}
            {big && height > 64 && <text x={x + 8} y={y + 55} fill="#fff" fillOpacity={0.85} fontSize={11}>{mask(fmtSigned(dollar))}</text>}
            {!big && width > 44 && height > 48 && <text x={x + 8} y={y + height / 2 + 18} fill="#fff" fontSize={10}>{fmtPct(pct)}</text>}
        </g>
    );
}

function TipRow({ label, value, cls = "" }) {
    return <div className="flex justify-between gap-4"><span className="text-muted-foreground">{label}</span><span className={`num font-medium ${cls}`}>{value}</span></div>;
}

function HoverCard({ active, payload, mask, stocks, period }) {
    const t = active && payload?.[0]?.payload;
    if (!t?.name) return null;
    const periodLabel = PERIODS.find((p) => p.key === period)?.label;
    return (
        <div className="rounded-lg border border-border bg-popover text-popover-foreground shadow-md px-3 py-2 text-xs space-y-0.5 min-w-[180px]" data-testid="heatmap-hover-card">
            <div className="font-semibold text-sm mb-1">{t.name}</div>
            {stocks ? (
                <>
                    <TipRow label="Shares" value={mask(t.shares?.toLocaleString("en-US", { maximumFractionDigits: 4 }))} />
                    <TipRow label="Price" value={fmtCurrency(t.price, { decimals: 2 })} />
                </>
            ) : <TipRow label="Positions" value={t.count} />}
            <TipRow label="Value" value={mask(fmtCurrency(t.mv))} />
            <TipRow label="Day's move" value={mask(fmtSigned(t.day))} cls={gainClass(t.day)} />
            {period !== "1D" && <TipRow label={`${periodLabel} move`} value={`${mask(fmtSigned(t.dollar))} · ${fmtPct(t.pct)}`} cls={gainClass(t.dollar)} />}
            {period === "1D" && <TipRow label="Day %" value={fmtPct(t.pct)} cls={gainClass(t.pct)} />}
        </div>
    );
}

function Seg({ options, value, onChange, prefix }) {
    return (
        <div className="inline-flex rounded-lg bg-muted p-0.5" data-testid={`${prefix}-toggle`}>
            {options.map((o) => (
                <button key={o.key} onClick={() => onChange(o.key)} data-testid={`${prefix}-${o.key}`}
                    className={`px-2 py-0.5 text-[11px] font-medium rounded-md transition-colors ${value === o.key ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

// Group positions by sub-portfolio or by ticker (a stock held in several subs becomes one tile)
function buildTiles(rows, mode) {
    const groups = {};
    for (const r of rows || []) {
        const key = mode === "stocks" ? r.ticker : r.sub_portfolio;
        const g = (groups[key] ||= { name: key, dollar: 0, prev: 0, shares: 0, day: 0, mv: 0, price: r.price, count: 0 });
        g.dollar += r.dollar;
        g.prev += r.start_value;
        g.shares += r.shares || 0;
        g.day += r.day_change || 0;
        g.mv += r.market_value || 0;
        g.count += 1;
    }
    const tiles = Object.values(groups).map((g) => ({ ...g, pct: g.prev ? (g.dollar / g.prev) * 100 : null }));
    const allFlat = tiles.every((t) => Math.abs(t.dollar) < 0.01);
    const totalAbs = tiles.reduce((t, r) => t + Math.abs(r.dollar), 0);
    // floor each tile so near-flat entries stay visible (smaller floor when there are many stocks)
    const floor = mode === "stocks" ? 0.02 : 0.08;
    return tiles.map((t) => ({ ...t, size: allFlat ? 1 : Math.max(Math.abs(t.dollar), totalAbs * floor) })).sort((a, b) => b.size - a.size);
}

/** Moves over a period: tile size = size of the $ move, colour = % move. Sub-portfolio or stock view. */
export function SubPortfolioHeatmap({ sub = "ALL", mask, height = 200, className = "", activeSub, onPickSub }) {
    const navigate = useNavigate();
    const [mode, setMode] = useState(() => stored("moringa-heatmap-mode", "subs"));
    const [period, setPeriod] = useState(() => stored("moringa-heatmap-period", "1D"));
    const [drill, setDrill] = useState(null);
    const q = useQuery({ queryKey: ["heatmap", sub, period], queryFn: async () => (await api.get(`/portfolio/heatmap?period=${period}&sub=${encodeURIComponent(sub)}`)).data });
    const view = drill ? "stocks" : mode;
    const data = buildTiles(drill ? (q.data?.rows || []).filter((r) => r.sub_portfolio === drill) : q.data?.rows, view);
    const openStock = (tk) => navigate(`/security/${tk}`);
    // 1st tap on a sub-portfolio filters Top Movers, 2nd tap drills into its stocks
    const pickSub = (n) => { if (n === activeSub || !onPickSub) setDrill(n); else onPickSub(n); };
    const onOpen = view === "stocks" ? openStock : pickSub;
    const back = () => { setDrill(null); onPickSub?.("ALL"); };
    const hint = drill ? " · tap a stock to open it" : mode === "stocks" ? " · tap a stock to open it" : onPickSub ? " · tap a sub-portfolio to filter Top Movers, tap again to see its stocks" : " · tap a sub-portfolio to see its stocks";
    return (
        <Card className={`p-4 ${className}`} data-testid="subportfolio-heatmap">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 min-w-0">
                    {drill && (
                        <button onClick={back} className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" data-testid="heatmap-back-btn">
                            <ChevronLeft className="w-3.5 h-3.5" />All
                        </button>
                    )}
                    <h2 className="font-heading text-base font-semibold truncate" data-testid="heatmap-title">{TITLES[period]}{drill && <span className="text-muted-foreground font-normal"> · {drill}</span>}</h2>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    <Seg options={PERIODS} value={period} onChange={(v) => { setPeriod(v); store("moringa-heatmap-period", v); }} prefix="heatmap-period" />
                    {!drill && <Seg options={MODES} value={mode} onChange={(v) => { setMode(v); store("moringa-heatmap-mode", v); }} prefix="heatmap-mode" />}
                </div>
            </div>
            {q.isLoading ? <Skeleton className="rounded-lg" style={{ height }} /> : data.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No price data for this period yet.</p> : (
                <ResponsiveContainer width="100%" height={height}>
                    <Treemap key={view + period + (drill || "")} data={data} dataKey="size" isAnimationActive={false} content={<Tile mask={mask} onOpen={onOpen} sat={SATURATE[period]} active={view === "subs" ? activeSub : undefined} />}>
                        <Tooltip content={<HoverCard mask={mask} stocks={view === "stocks"} period={period} />} isAnimationActive={false} />
                    </Treemap>
                </ResponsiveContainer>
            )}
            <p className="hidden sm:block text-[10px] text-muted-foreground mt-1.5">Size = $ move · colour = % change over the period, on current shares{hint}</p>
        </Card>
    );
}
